import { useState } from 'react';
import {
  Anchor,
  Button,
  Group,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { Link } from 'react-router';
import {
  BOS_PTW_IZIN_SUZGECI,
  PTW_SAYFA_BOYUTU,
  PTW_SUPER_DURUMLARI,
  etkinPtwSuzgeciSayisi,
} from '@isg/validation';
import type { PtwIzinSuzgeci } from '@isg/validation';
import {
  izinListesiCiktiYolu,
  useCiktiIndir,
  useIzinSuzgecSecenekleri,
  useIzinler,
  useKapilar,
} from '../api/queries';
import { PTW_SUPER_DURUM_ETIKET } from '../i18n/tr';
import { gunGirdisiIso } from '../lib/format';
import { Sayfalama, SuzgecGostergesi, KapiSebebi } from '../sozlesme';
import { GecerlilikHucresi, damga, durumRozeti } from './izin-parts';
import { BosListeNotu, QueryStatus } from './parts';

/**
 * İŞ İZİNLERİ — süper-durum sekmeleri + sunucu süzgeci (F3·S4; ✏️ tur 10 / K5).
 *
 * SEKMELER SÜPER-DURUMDUR, token değil: 11 token sahada okunmaz, 5 şapka
 * okunur. Süper-durum KOLON DEĞİLDİR — sunucu `ptwSuperDurumOf` ile türetir.
 *
 * ✏️ **K5: SÜZGEÇ VE SAYFALAMA SUNUCUDA.** Ekran eskiden görünen HER izni
 * çekip sekmeyi JS'te süzüyordu; *"şu anki süzgeçle N kayıt"* cümlesi
 * kurulamıyordu ve Excel çıktısı bu yüzden mümkün değildi (P4 → S6.5 bağı).
 *
 * SAYAÇLAR SÜZGECİN İÇİNDE, SEKMENİN DIŞINDA sayılır: seçili sekme yalnız
 * satırları daraltır, sayılar diğer sekmelerin de dolu olduğunu söyler —
 * yoksa *"sahada 0"* ile *"sahada boş sekme"* ayırt edilemez.
 *
 * MÜDÜR BİLGİLENDİRMESİ BU EKRANDIR (kilitli karar): bozulan kontrolün
 * kırmızı damgası burada görünür; ayrıca yönetici bildirimi üretilmez.
 */
export function IzinlerScreen(): React.JSX.Element {
  const [suzgec, setSuzgec] = useState<PtwIzinSuzgeci>(BOS_PTW_IZIN_SUZGECI);
  const izinler = useIzinler(suzgec);
  const secenekler = useIzinSuzgecSecenekleri();
  const indir = useCiktiIndir();
  const sayaclar = izinler.data?.sayaclar;
  // P13-b (madde 225): talep açma kapısı SUNUCUDAN (iç kullanıcılar; bahçe hedef
  // seçilince iznin kendi ucunda) — ekran şapka dizgesi yazmaz.
  const kapilar = useKapilar();
  const talepAcabilir = kapilar.data?.izinTalepKapisi.yazabilir ?? false;

  // Süzgeç değişince SAYFA 1'E DÖNER: 4. sayfadayken daraltma yapan kullanıcı
  // aksi hâlde boş bir sayfaya düşer ve listeyi boş sanır.
  const daralt = (yama: Partial<PtwIzinSuzgeci>): void =>
    setSuzgec((mevcut) => ({ ...mevcut, ...yama, sayfa: 1 }));

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>İş izinleri</Title>
        <Group gap="xs">
          <Button
            variant="default"
            loading={indir.isPending}
            onClick={() =>
              indir.mutate({ yol: izinListesiCiktiYolu(suzgec), yedekAd: 'is-izinleri.xlsx' })
            }
            data-testid="izin-listesi-excel"
          >
            Excel'e aktar
          </Button>
          {talepAcabilir && (
            <Button component={Link} to="/izinler/yeni" data-testid="izin-talebi-ac">
              Yeni izin talebi
            </Button>
          )}
          {kapilar.data && (
            <KapiSebebi
              durum={kapilar.data.izinTalepKapisi}
              nesne="İzin talebini"
              fiil="açar"
              testId="izin-talebi-yazamama-sebebi"
            />
          )}
        </Group>
      </Group>
      {/* ⚠️ **K7.4 — İÇ DİL CÜMLESİ KALDIRILDI (M1).** Eski metin *"Sekmeler
          süper-durumdur (11 token 5 şapkaya iner)"* diyordu: "süper-durum" ve
          "token" bu ürünün İÇ kavramlarıdır, kullanıcının sözlüğünde yoktur ve
          cümle ona hiçbir şey öğretmiyordu. Kalan cümle yalnız damganın ne
          anlama geldiğini söylüyor — çünkü onu ekrandan çıkarmanın yolu yok. */}
      <Text size="sm" c="dimmed">
        Kırmızı satır, izin sürerken bir kontrolün bozulduğunu ve izin verenin kararının
        beklendiğini söyler.
      </Text>

      <Tabs
        value={suzgec.superDurum}
        onChange={(deger) => daralt({ superDurum: deger as PtwIzinSuzgeci['superDurum'] })}
        data-testid="izin-sekmeleri"
      >
        <Tabs.List>
          {PTW_SUPER_DURUMLARI.map((sd) => (
            <Tabs.Tab key={sd} value={sd} data-testid={`izin-sekme-${sd}`}>
              {PTW_SUPER_DURUM_ETIKET[sd]}
              {sayaclar ? ` (${sayaclar[sd]})` : ''}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>

      {/* SÜZGEÇ ŞERİDİ (P4) — havuzlar sunucudan; bir süzgeç, sonuç
          üretmeyecek adı hiç göstermez (uç yalnız KULLANILMIŞ değerleri
          döner). */}
      <Group align="end" gap="sm" data-testid="izin-suzgecleri">
        <Select
          label="İşyeri"
          placeholder="Tümü"
          clearable
          searchable
          w={200}
          data={(secenekler.data?.isyerleri ?? []).map((i) => ({ value: i.id, label: i.ad }))}
          value={suzgec.workplaceId}
          onChange={(deger) => daralt({ workplaceId: deger })}
          data-testid="izin-suzgec-isyeri"
        />
        <Select
          label="İzin türü"
          placeholder="Tümü"
          clearable
          searchable
          w={200}
          data={(secenekler.data?.izinTurleri ?? []).map((t) => ({ value: t.id, label: t.ad }))}
          value={suzgec.izinTuruRefId}
          onChange={(deger) => daralt({ izinTuruRefId: deger })}
          data-testid="izin-suzgec-tur"
        />
        <Select
          label="Talep eden"
          placeholder="Tümü"
          clearable
          searchable
          w={200}
          data={(secenekler.data?.talepEdenler ?? []).map((k) => ({ value: k.id, label: k.ad }))}
          value={suzgec.talepEdenPersonId}
          onChange={(deger) => daralt({ talepEdenPersonId: deger })}
          data-testid="izin-suzgec-talep-eden"
        />
        {/* TARİH ARALIĞI KESİŞİM SORAR: 1-15 Eylül diyen kullanıcı, 28
            Ağustos'ta açılıp 3 Eylül'de biten izni de kastediyor (kural
            sunucuda, `ptwListeWhere`). Giriş TAKVİMDEN (madde 137 deseni) ve
            yalnız GEÇERLİ gün süzgece iner — yarım yazılmış bir tarih
            listeyi sessizce boşaltmaz. */}
        <TextInput
          label="Başlangıç (bu tarihten sonra)"
          type="date"
          w={190}
          value={suzgec.baslangicDan ?? ''}
          onChange={(e) => {
            const deger = e.currentTarget.value;
            daralt({ baslangicDan: gunGirdisiIso(deger) === null ? null : deger });
          }}
          data-testid="izin-suzgec-baslangic"
        />
        <TextInput
          label="Bitiş (bu tarihe kadar)"
          type="date"
          w={190}
          value={suzgec.bitisEKadar ?? ''}
          onChange={(e) => {
            const deger = e.currentTarget.value;
            daralt({ bitisEKadar: gunGirdisiIso(deger) === null ? null : deger });
          }}
          data-testid="izin-suzgec-bitis"
        />
        <SuzgecGostergesi
          etkinSayisi={etkinPtwSuzgeciSayisi(suzgec)}
          onTemizle={() => setSuzgec({ ...BOS_PTW_IZIN_SUZGECI, superDurum: suzgec.superDurum })}
          testId="izin-suzgec-gostergesi"
        />
      </Group>

      <QueryStatus queries={[izinler]} />
      {izinler.isSuccess && izinler.data.permits.length === 0 && (
        <BosListeNotu mesaj="Bu durumda iş izni yok." />
      )}
      {izinler.isSuccess && izinler.data.permits.length > 0 && (
        <>
          <Table striped highlightOnHover data-testid="izin-listesi">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>İşyeri / lokasyon</Table.Th>
                {/* K5: İZİN TÜRÜ KOLONU — *"bu hangi iş"* sorusunun cevabı
                    eskiden ancak detaya girilerek öğreniliyordu. */}
                <Table.Th>İzin türü</Table.Th>
                <Table.Th>Talep eden</Table.Th>
                <Table.Th>Geçerlilik</Table.Th>
                <Table.Th>Durum</Table.Th>
                <Table.Th>Damga</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {izinler.data.permits.map((permit) => (
                <Table.Tr
                  key={permit.id}
                  data-testid="izin-satiri"
                  data-permit={permit.id}
                  // Damgalı satır KIRMIZI: listede tek bakışta ayrışması gerek.
                  bg={permit.kararBekliyor ? 'var(--mantine-color-red-0)' : undefined}
                >
                  <Table.Td>
                    <Anchor component={Link} to={`/izinler/${permit.id}`} size="sm">
                      {permit.workplaceAd}
                    </Anchor>
                    <Text size="xs" c="dimmed">
                      {permit.locationAd}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" data-testid="izin-satiri-tur">
                      {/* Türü olmayan taslak "—" der: boş hücre, türün
                          yüklenmediği izlenimi bırakırdı. */}
                      {permit.izinTurleri.join(', ') || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>{permit.talepEdenAd}</Table.Td>
                  <Table.Td>
                    <GecerlilikHucresi permit={permit} />
                  </Table.Td>
                  <Table.Td>{durumRozeti(permit)}</Table.Td>
                  <Table.Td>{damga(permit)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Sayfalama
            sayfa={izinler.data.sayfa}
            sayfaBoyutu={izinler.data.sayfaBoyutu ?? PTW_SAYFA_BOYUTU}
            toplam={izinler.data.toplam}
            onDegis={(sayfa) => setSuzgec((mevcut) => ({ ...mevcut, sayfa }))}
            testId="izin-sayfalama"
          />
        </>
      )}
    </Stack>
  );
}
